import { LM, sideLandmarks } from '@erectus/shared';
import type { Episode, FrameRecord, FrameState, Side } from '@erectus/shared';
import { FRAME_STRIDE, QUANT_VIS, QUANT_XYZ } from '@/session/db.js';
import { PORTRAIT_IDEAL } from '@/lib/camera.js';

/**
 * The pure half of the skeleton replay: which frame is on screen at a moment,
 * how two stored frames blend between samples, where the person sat across the
 * whole session, and how fast a long session should play.
 *
 * Everything here works on the int16 landmarks already kept on the device for
 * the report — the replay never had a picture to show (CLAUDE.md principle 1).
 */

/** Playback speeds, as multiples of real time. A 2-hour session at 60× is 2 minutes. */
export const REPLAY_SPEEDS = [10, 60, 240] as const;
export type ReplaySpeed = (typeof REPLAY_SPEEDS)[number];

/** The default speed is the slowest one that gets through the session in this long. */
const TARGET_PLAYBACK_MS = 2 * 60_000;

/** Two samples further apart than this are not blended: something happened between them. */
const MAX_BLEND_GAP_MS = 2000;

/** Away time shorter than this is not worth announcing when playback skips it. */
export const ANNOUNCE_SKIP_MS = 60_000;

const VISIBLE = 0.3;

export function defaultSpeed(durationMs: number): ReplaySpeed {
  return REPLAY_SPEEDS.find((s) => durationMs / s <= TARGET_PLAYBACK_MS) ?? REPLAY_SPEEDS[REPLAY_SPEEDS.length - 1]!;
}

export function measurable(state: FrameState): boolean {
  return state !== 'invalid';
}

/** Index of the last frame at or before `t` (0 when `t` is before the first). */
export function frameIndexAt(frames: readonly FrameRecord[], t: number): number {
  let lo = 0;
  let hi = frames.length - 1;
  while (lo < hi) {
    const mid = (lo + hi + 1) >> 1;
    if (frames[mid]!.t <= t) lo = mid;
    else hi = mid - 1;
  }
  return lo;
}

/**
 * When `t` falls in time the person was out of view, the time playback should
 * jump to: the next measurable frame. `null` when `t` is measurable already or
 * nothing measurable follows.
 */
export function skipAwayFrom(frames: readonly FrameRecord[], t: number): number | null {
  if (frames.length === 0) return null;
  const i = frameIndexAt(frames, t);
  if (measurable(frames[i]!.state)) return null;
  for (let j = i + 1; j < frames.length; j += 1) {
    if (measurable(frames[j]!.state)) return frames[j]!.t;
  }
  return null;
}

/** Start of the nearest slip after (`dir` 1) or before (`dir` -1) `t`, in ms. */
export function adjacentEpisode(episodes: readonly Episode[], t: number, dir: 1 | -1): number | null {
  const starts = episodes.map((e) => e.start * 1000).sort((a, b) => a - b);
  // A little slack backwards so "previous" from just after a slip's start goes to the one before it.
  if (dir === 1) return starts.find((s) => s > t + 500) ?? null;
  return [...starts].reverse().find((s) => s < t - 1500) ?? null;
}

export interface Point {
  x: number;
  y: number;
  ok: boolean;
}

/** Joints of the camera-facing side, in portrait-corrected frame units (x scaled by 3:4). */
export interface Joints {
  ear: Point;
  shoulder: Point;
  elbow: Point;
  wrist: Point;
  hip: Point;
  knee: Point;
  ankle: Point;
}

export const BONES: ReadonlyArray<readonly [keyof Joints, keyof Joints]> = [
  ['ear', 'shoulder'],
  ['shoulder', 'hip'],
  ['hip', 'knee'],
  ['knee', 'ankle'],
  ['shoulder', 'elbow'],
  ['elbow', 'wrist'],
];

const ASPECT = PORTRAIT_IDEAL.width / PORTRAIT_IDEAL.height;

/**
 * One landmark in portrait-corrected units, levelled by the phone's roll the
 * way the live pipeline levels before measuring (pose/level.ts): stored frames
 * keep the picture's own tilt, and the replay's upright reference line should
 * be true vertical, as the angles are.
 */
function point(lm: Int16Array, i: number, rollDeg = 0): Point {
  const base = i * FRAME_STRIDE;
  const x = ((lm[base] ?? 0) / QUANT_XYZ) * ASPECT;
  const y = (lm[base + 1] ?? 0) / QUANT_XYZ;
  const ok = (lm[base + 3] ?? 0) / QUANT_VIS > VISIBLE;
  if (rollDeg === 0) return { x, y, ok };
  const a = (rollDeg * Math.PI) / 180;
  const dx = x - 0.5 * ASPECT;
  const dy = y - 0.5;
  return { x: dx * Math.cos(a) + dy * Math.sin(a) + 0.5 * ASPECT, y: -dx * Math.sin(a) + dy * Math.cos(a) + 0.5, ok };
}

export function jointsOf(lm: Int16Array, side: Side, rollDeg = 0): Joints | null {
  if (lm.length === 0) return null;
  const idx = sideLandmarks(side);
  return {
    ear: point(lm, idx.ear, rollDeg),
    shoulder: point(lm, idx.shoulder, rollDeg),
    elbow: point(lm, idx.elbow, rollDeg),
    wrist: point(lm, idx.wrist, rollDeg),
    hip: point(lm, idx.hip, rollDeg),
    knee: point(lm, idx.knee, rollDeg),
    ankle: point(lm, idx.ankle, rollDeg),
  };
}

function blend(a: Joints, b: Joints, w: number): Joints {
  const out = {} as Joints;
  for (const key of Object.keys(a) as Array<keyof Joints>) {
    const p = a[key];
    const q = b[key];
    out[key] =
      p.ok && q.ok
        ? { x: p.x + (q.x - p.x) * w, y: p.y + (q.y - p.y) * w, ok: true }
        : w < 0.5
          ? p
          : q;
  }
  return out;
}

export interface ReplayMoment {
  index: number;
  state: FrameState;
  cva: number | null;
  trunk: number | null;
  joints: Joints | null;
}

/**
 * What the replay shows at `t`: the frame at or before it, with the skeleton
 * blended toward the next sample so slow speeds move smoothly instead of
 * stepping at the 2–5 fps the session was read at.
 */
export function momentAt(frames: readonly FrameRecord[], t: number, side: Side, rollDeg = 0): ReplayMoment | null {
  if (frames.length === 0) return null;
  const index = frameIndexAt(frames, t);
  const frame = frames[index]!;
  const base = { index, state: frame.state, cva: frame.cva, trunk: frame.trunk };
  if (!measurable(frame.state)) return { ...base, joints: null };

  const joints = jointsOf(frame.lm, side, rollDeg);
  const next = frames[index + 1];
  if (!joints || !next || !measurable(next.state) || next.t - frame.t > MAX_BLEND_GAP_MS || t <= frame.t) {
    return { ...base, joints };
  }
  const after = jointsOf(next.lm, side, rollDeg);
  if (!after) return { ...base, joints };
  const w = Math.min(1, (t - frame.t) / (next.t - frame.t));
  return { ...base, joints: blend(joints, after, w) };
}

/** Measurable frames, thinned so whole-session passes stay cheap on a 3-hour session. */
function sample(frames: readonly FrameRecord[], max = 1500): FrameRecord[] {
  const usable = frames.filter((f) => measurable(f.state) && f.lm.length > 0);
  const step = Math.max(1, Math.floor(usable.length / max));
  return usable.filter((_, i) => i % step === 0);
}

export interface ReplayStage {
  side: Side;
  /** The session's median phone roll, levelled out of every skeleton. */
  rollDeg: number;
  /** True when the person faced left in the frame; the replay mirrors them to face right. */
  facingLeft: boolean;
  /** Portrait-corrected frame units covering where the body was through the session. */
  bounds: { minX: number; maxX: number; minY: number; maxY: number };
  /** Median shoulder–hip length in the same units; sizes the drawn head. `null` when never seen. */
  torso: number | null;
}

/**
 * Head radius as a share of shoulder–hip length, so the drawn head keeps a
 * body's proportions at any zoom. A drawing constant, not a threshold: an
 * adult head is about 20 cm front to back, shoulder joint to hip joint about
 * 50 cm (rounded from adult anthropometric tables).
 */
export const HEAD_PER_TORSO = 0.2;

/**
 * One fixed view for the whole session. Fitting each frame to the box (as the
 * ten-minute cards do) would hide exactly what the replay is for — the head
 * drifting forward, the trunk folding — so the box is set once, from where the
 * joints were across the session, trimmed of the rare wild detection.
 */
export function stageFor(frames: readonly FrameRecord[], rollDeg = 0): ReplayStage | null {
  const rows = sample(frames);
  if (rows.length === 0) return null;

  let leftEar = 0;
  for (const f of rows) {
    if ((f.lm[LM.LEFT_EAR * FRAME_STRIDE + 3] ?? 0) >= (f.lm[LM.RIGHT_EAR * FRAME_STRIDE + 3] ?? 0)) leftEar += 1;
  }
  const side: Side = leftEar * 2 >= rows.length ? 'left' : 'right';

  const xs: number[] = [];
  const ys: number[] = [];
  let facingLeft = 0;
  let facingKnown = 0;
  const torsos: number[] = [];
  for (const f of rows) {
    const j = jointsOf(f.lm, side, rollDeg);
    if (!j) continue;
    if (j.shoulder.ok && j.hip.ok) torsos.push(Math.hypot(j.shoulder.x - j.hip.x, j.shoulder.y - j.hip.y));
    for (const p of Object.values(j)) {
      if (!p.ok) continue;
      xs.push(p.x);
      ys.push(p.y);
    }
    const nose = point(f.lm, LM.NOSE, rollDeg);
    if (nose.ok && j.ear.ok) {
      facingKnown += 1;
      if (nose.x < j.ear.x) facingLeft += 1;
    }
  }
  if (xs.length === 0) return null;

  const trimmed = (values: number[]): [number, number] => {
    const sorted = [...values].sort((a, b) => a - b);
    const at = (q: number): number => sorted[Math.min(sorted.length - 1, Math.floor(q * sorted.length))]!;
    return [at(0.01), at(0.99)];
  };
  const [minX, maxX] = trimmed(xs);
  const [minY, maxY] = trimmed(ys);
  torsos.sort((a, b) => a - b);
  const torso = torsos.length > 0 ? torsos[Math.floor(torsos.length / 2)]! : null;
  return { side, rollDeg, facingLeft: facingLeft * 2 > facingKnown, bounds: { minX, maxX, minY, maxY }, torso };
}

/**
 * The faint figure behind the live one: the person sitting well earlier in the
 * same session — the good frame whose neck angle is the median of the good
 * frames, so it is typical rather than one lucky reading. `null` when the
 * session never had good posture.
 */
export function referenceFrame(frames: readonly FrameRecord[]): FrameRecord | null {
  const good = frames.filter((f) => f.state === 'good' && f.cva !== null && f.lm.length > 0);
  if (good.length === 0) return null;
  const sorted = [...good].sort((a, b) => a.cva! - b.cva!);
  return sorted[Math.floor(sorted.length / 2)]!;
}

/** Shift a skeleton so its hip sits on `hip` — the chair may have moved, the comparison is the spine above it. */
export function alignAtHip(joints: Joints, hip: Point): Joints {
  if (!joints.hip.ok || !hip.ok) return joints;
  const dx = hip.x - joints.hip.x;
  const dy = hip.y - joints.hip.y;
  const out = {} as Joints;
  for (const key of Object.keys(joints) as Array<keyof Joints>) {
    const p = joints[key];
    out[key] = { x: p.x + dx, y: p.y + dy, ok: p.ok };
  }
  return out;
}
